import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  RefreshCw,
  Activity,
  Loader2,
  ExternalLink,
  Zap,
  Plus,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────

interface UptimeMonitor {
  id: number;
  name: string;
  url: string;
  status: 'up' | 'down' | 'seems_down' | 'paused' | 'pending' | 'unknown';
  uptime_ratio: string;
  avg_response_ms: string;
}

interface HealthCheck {
  status: 'ok' | 'degraded' | 'down';
  timestamp: string;
  platform: string;
  total_latency_ms: number;
  checks: Record<string, { status: string; latency_ms?: number; error?: string }>;
}

// ─── Status config ────────────────────────────────────────

const monitorStatusCfg = {
  up:          { label: 'Online',       icon: CheckCircle2, className: 'bg-[hsl(168,56%,42%)]/10 text-[hsl(168,56%,42%)] border-[hsl(168,56%,42%)]/20' },
  seems_down:  { label: 'Instável',     icon: AlertCircle,  className: 'bg-[hsl(45,90%,51%)]/10 text-[hsl(45,90%,51%)] border-[hsl(45,90%,51%)]/20' },
  down:        { label: 'Offline',      icon: XCircle,      className: 'bg-destructive/10 text-destructive border-destructive/20' },
  paused:      { label: 'Pausado',      icon: AlertCircle,  className: 'bg-muted/60 text-muted-foreground border-border/40' },
  pending:     { label: 'Verificando',  icon: Loader2,      className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  unknown:     { label: 'Desconhecido', icon: AlertCircle,  className: 'bg-muted/60 text-muted-foreground border-border/40' },
};

const healthStatusCfg = {
  ok:       { label: 'Operacional',   icon: CheckCircle2, dot: 'bg-[hsl(168,56%,42%)]', text: 'text-[hsl(168,56%,42%)]' },
  degraded: { label: 'Degradado',    icon: AlertCircle,  dot: 'bg-[hsl(45,90%,51%)]',  text: 'text-[hsl(45,90%,51%)]' },
  down:     { label: 'Indisponível', icon: XCircle,      dot: 'bg-destructive',          text: 'text-destructive' },
};

// ─── Monitor Card ─────────────────────────────────────────

const MonitorCard: React.FC<{ monitor: UptimeMonitor }> = ({ monitor }) => {
  const cfg = monitorStatusCfg[monitor.status] || monitorStatusCfg.unknown;
  const Icon = cfg.icon;

  return (
    <div className="emp-card p-4 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-medium text-foreground truncate">{monitor.name}</p>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${cfg.className}`}>
            <Icon className="h-3 w-3" />
            {cfg.label}
          </span>
        </div>
        <p className="text-xs text-muted-foreground font-mono truncate">{monitor.url}</p>
        <div className="flex items-center gap-4 mt-2">
          <span className="text-xs text-muted-foreground">
            Uptime: <span className="text-foreground font-semibold">{monitor.uptime_ratio !== '—' ? `${monitor.uptime_ratio}%` : '—'}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            Resp. média: <span className="text-foreground font-semibold">{monitor.avg_response_ms !== '—' ? `${monitor.avg_response_ms}ms` : '—'}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Health Detail ────────────────────────────────────────

const HealthDetail: React.FC<{ health: HealthCheck }> = ({ health }) => {
  const checkLabel: Record<string, string> = {
    database:       'Banco de Dados',
    edge_functions: 'Edge Functions',
    storage:        'Armazenamento',
  };

  return (
    <div className="emp-card p-4 space-y-3">
      <h3 className="emp-section-title">Diagnóstico Interno — Tempo Real</h3>
      <div className="space-y-2">
        {Object.entries(health.checks).map(([key, check]) => {
          const isOk = check.status === 'ok';
          return (
            <div key={key} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
              <div className="flex items-center gap-2">
                {isOk
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(168,56%,42%)]" />
                  : <AlertCircle className="h-3.5 w-3.5 text-[hsl(45,90%,51%)]" />}
                <span className="text-sm">{checkLabel[key] || key}</span>
              </div>
              <div className="flex items-center gap-3">
                {check.latency_ms !== undefined && (
                  <span className="text-xs text-muted-foreground">{check.latency_ms}ms</span>
                )}
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                  isOk
                    ? 'bg-[hsl(168,56%,42%)]/10 text-[hsl(168,56%,42%)] border-[hsl(168,56%,42%)]/20'
                    : 'bg-[hsl(45,90%,51%)]/10 text-[hsl(45,90%,51%)] border-[hsl(45,90%,51%)]/20'
                }`}>
                  {isOk ? 'ok' : check.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Latência total: {health.total_latency_ms}ms · Verificado em {new Date(health.timestamp).toLocaleString('pt-BR')}
      </p>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────

const SlaUptimePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { role } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [hasApiKey, setHasApiKey] = useState(true);

  // ── Health check interno ─────────────────────────────────
  // NOTE: ALL hooks must be called before any conditional return (Rules of Hooks)
  const { data: health, isLoading: loadingHealth } = useQuery<HealthCheck | null>({
    queryKey: ['sla-health'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('health-check');
        if (error || !data) return null;
        return data as HealthCheck;
      } catch {
        return null;
      }
    },
    refetchInterval: 60000,
    enabled: ['admin', 'coadmin'].includes(role || ''),
  });

  // ── Uptime Robot monitors ────────────────────────────────
  const { data: monitorsData, isLoading: loadingMonitors } = useQuery({
    queryKey: ['sla-monitors'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('uptime-robot-proxy');
        // Any error from the function (incl. invalid api key) → show setup UI
        if (error || data?.error) {
          setHasApiKey(false);
          return { monitors: [] as UptimeMonitor[] };
        }
        return data as { monitors: UptimeMonitor[] };
      } catch {
        setHasApiKey(false);
        return { monitors: [] as UptimeMonitor[] };
      }
    },
    refetchInterval: 120000,
    retry: false,
    enabled: ['admin', 'coadmin'].includes(role || ''),
  });

  // ── Setup monitors ───────────────────────────────────────
  const { mutate: setupMonitors, isPending: settingUp } = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('uptime-robot-proxy', {
        body: { action: 'setup' },
      });
      if (error) {
        const msg = error.message || 'Erro na edge function';
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(String(data.error));
      }
      return data;
    },
    onSuccess: (data) => {
      const created = (data?.results ?? []).filter((r: { success: boolean }) => r.success).length;
      toast({ title: `${created} monitores criados no Uptime Robot` });
      qc.invalidateQueries({ queryKey: ['sla-monitors'] });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Tente novamente mais tarde';
      toast({ title: 'Erro ao criar monitores', description: message, variant: 'destructive' });
    },
  });

  // ── Access guard (after all hooks) ───────────────────────
  if (!['admin', 'coadmin'].includes(role || '')) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Acesso restrito a administradores.
      </div>
    );
  }

  const monitors: UptimeMonitor[] = monitorsData?.monitors || [];
  const overallStatus = health?.status || 'ok';
  const statusCfg = healthStatusCfg[overallStatus as keyof typeof healthStatusCfg] || healthStatusCfg.ok;
  const StatusIcon = statusCfg.icon;

  return (
    <div className="h-full flex flex-col gap-4 overflow-auto">
      {/* Header — hidden when embedded as tab in Observabilidade */}
      {!embedded && (
      <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
        <div>
          <h2 className="text-base font-semibold text-foreground">SLA & Uptime</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5">Monitoramento contínuo dos componentes críticos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-8"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['sla-health'] });
              qc.invalidateQueries({ queryKey: ['sla-monitors'] });
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Atualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-8"
            onClick={() => window.open('https://uptimerobot.com/dashboard', '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Uptime Robot
          </Button>
        </div>
      </div>
      )}

      {/* Status geral */}
      <div className="emp-card p-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${statusCfg.dot} ${overallStatus !== 'down' ? 'animate-pulse' : ''}`} />
          <div>
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-4 w-4 ${statusCfg.text}`} />
              <span className={`text-sm font-semibold ${statusCfg.text}`}>{statusCfg.label}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              NetUno – Menos riscos, melhores decisões, maior previsibilidade
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Monitores Ativos', value: monitors.length,                                        icon: Activity,     color: 'text-foreground' },
          { label: 'Online',           value: monitors.filter(m => m.status === 'up').length,         icon: CheckCircle2, color: 'text-[hsl(168,56%,42%)]' },
          { label: 'Instáveis',        value: monitors.filter(m => m.status === 'seems_down').length, icon: AlertCircle,  color: 'text-[hsl(45,90%,51%)]' },
          { label: 'Offline',          value: monitors.filter(m => m.status === 'down').length,       icon: XCircle,      color: 'text-destructive' },
        ].map(c => (
          <div key={c.label} className="emp-stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="emp-kpi-label">{c.label}</p>
                <p className={`text-2xl font-bold mt-1 ${c.color}`}>{c.value}</p>
              </div>
              <div className="emp-icon-badge">
                <c.icon className={`h-4 w-4 ${c.color}`} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monitores Uptime Robot */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="emp-section-title">Monitores Uptime Robot</h3>
            {hasApiKey && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 h-7 text-xs"
                onClick={() => setupMonitors()}
                disabled={settingUp}
              >
                {settingUp ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Criar Monitores
              </Button>
            )}
          </div>

          {!hasApiKey ? (
            <div className="emp-card p-6 text-center space-y-3">
              <Zap className="h-8 w-8 mx-auto text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">API Key não configurada</p>
                <p className="text-[12px] text-muted-foreground mt-1">
                  Adicione a chave <code className="bg-muted px-1 rounded">UPTIME_ROBOT_API_KEY</code> para ativar o monitoramento externo.
                </p>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>1. Acesse <strong>uptimerobot.com</strong> e crie uma conta gratuita</p>
                <p>2. Vá em <strong>My Settings → API Settings</strong></p>
                <p>3. Copie a <strong>Main API Key</strong></p>
                <p>4. Adicione o secret <code className="bg-muted px-1 rounded">UPTIME_ROBOT_API_KEY</code></p>
              </div>
            </div>
          ) : loadingMonitors ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <div key={i} className="emp-card p-4"><Skeleton className="h-16" /></div>)}
            </div>
          ) : monitors.length === 0 ? (
            <div className="emp-card p-6 text-center">
              <p className="text-sm text-muted-foreground">Nenhum monitor configurado.</p>
              <p className="text-xs text-muted-foreground mt-1">Clique em "Criar Monitores" para configurar automaticamente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {monitors.map(m => <MonitorCard key={m.id} monitor={m} />)}
            </div>
          )}
        </div>

        {/* Health check interno */}
        <div className="space-y-3">
          <h3 className="emp-section-title">Diagnóstico Interno</h3>
          {loadingHealth ? (
            <div className="emp-card p-4"><Skeleton className="h-40" /></div>
          ) : health ? (
            <HealthDetail health={health} />
          ) : (
            <div className="emp-card p-4 text-center text-sm text-muted-foreground">
              Falha ao carregar diagnóstico.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlaUptimePage;
